import React, { useCallback, useState, useRef, useEffect } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import Webcam from "react-webcam";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  IconButton,
  ImageListItemBar,
  Stack,
  Typography,
  InputLabel,
} from "@mui/material";
import { FaFileUpload } from "@react-icons/all-files/fa/FaFileUpload";
import { GrDocumentUpload } from "@react-icons/all-files/gr/GrDocumentUpload";
import { FaCamera } from "@react-icons/all-files/fa/FaCamera";
import { FaTrash } from "@react-icons/all-files/fa/FaTrash";
import { useTranslation } from "react-i18next";
import { RedAsterisk } from "../../../styles/controls"; 
import MessageViewer from "@shared/messageViewer";
import theme from "config/theme";

interface FileUploaderWithCameraProps {
  onChange: (file: File | null) => void;
  value: File | string | null;
  label?: string;
  error?: string;
}

const FileUploaderWithCamera: React.FC<FileUploaderWithCameraProps> = ({
  onChange,
  value,
  label,
  error,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dropzoneError, setDropzoneError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        setPreview(value);
      } else {
        const objectUrl = URL.createObjectURL(value);
        setPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      }
    } else {
      setPreview(null);
    }
  }, [value]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setDropzoneError(null);
      setCameraError(null);
      console.log('onDrop triggered. Files:', acceptedFiles);

      if (fileRejections.length > 0) {
        const firstError = fileRejections[0].errors[0];
        if (firstError.code === "file-too-large") {
          setDropzoneError("File is larger than 1MB. Please upload a smaller file.");
        } else if (firstError.code === "file-invalid-type") {
          setDropzoneError("Invalid file type. Please upload a JPG or PNG file.");
        } else {
          setDropzoneError(firstError.message);
        }
      } else if (acceptedFiles.length > 0) {
        onChange(acceptedFiles[0]);
      }
      setIsModalOpen(false); // Close modal after selection/rejection
    },
    [onChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    multiple: false,
    maxSize: 1048576, // 1MB in bytes
  });

  const handleCapture = useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;

    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // If image is portrait, rotate it to be landscape
      if (image.height > image.width) {
        canvas.width = image.height;
        canvas.height = image.width;
        ctx.rotate(90 * Math.PI / 180);
        ctx.translate(0, -image.height);
      } else {
        canvas.width = image.width;
        canvas.height = image.height;
      }
      ctx.drawImage(image, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
          onChange(file);
          setIsCameraOpen(false);
          setIsModalOpen(false);
        }
      }, 'image/jpeg');
    }
  }, [webcamRef, onChange]);

  const openCamera = () => {
    setCameraError(null);
    setIsCameraOpen(true);
  };
  const closeCamera = () => setIsCameraOpen(false);

  const handleCameraError = (error: any) => {
    if (error.name === 'NotAllowedError') {
      setCameraError("Camera access is required. Please grant camera permission in your browser settings and try again.");
    } else {
      setCameraError(`An error occurred while accessing the camera: ${error.message}`);
    }
    closeCamera();
  };

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropzoneError(null);
    setCameraError(null);
    onChange(null);
  };

  return (
    <div>
      {label && (
        <>
          <InputLabel>
            {label}
            <RedAsterisk>&thinsp;*</RedAsterisk>
          </InputLabel>
          <Typography variant="body2" color={"#333"}>{"The image must clearly show the account holder name, account number, and IFSC code."}</Typography>
        </>
      )}
      <Box
        onClick={() => setIsModalOpen(true)}
        sx={{
          position: "relative",
          border: `2px dashed ${theme.palette.primary.main}`,
          borderRadius: 1,
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: "rgba(0, 124, 191, 0.08)",
          minHeight: "150px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundImage: preview ? `url(${preview})` : "none",
        }}
      >
        {/* This Box is now just a clickable display area */}
        {value && preview ? (
          <ImageListItemBar
            sx={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
            title={typeof value === 'string' ? value.split('/').pop() : value.name}
            actionIcon={<IconButton sx={{ color: "red", background:'#fff' }} onClick={removeFile}><FaTrash size={15} /></IconButton>}
          />
        ) : (
          <Stack alignItems="center" spacing={1}>
            <style>
              {`
                svg path {
                  stroke: ${theme.palette.primary.main}
                }
                `}
            </style>
            <GrDocumentUpload size={24} />
            
            <Typography>
              {t("drag_drop_file", {
                defaultValue: "Click here to upload",
              })}
            </Typography>
            <Typography variant="body2" color={"#333"}>JPG, PNG (max. 1 MB)</Typography>
          </Stack>
        )}
      </Box>
      {(error || dropzoneError || cameraError) && <MessageViewer status="error" details={error || dropzoneError || cameraError || ''} />}

      {/* Modal for upload options */}
      <Drawer
        anchor="bottom"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Stack p={2} spacing={2}>
          <Typography variant="h6" textAlign="center">
            {t("upload_cheque", { defaultValue: "Upload cancelled cheque or passbook photo" })}
          </Typography>
          {/* The getInputProps are now on a hidden input, triggered by the button */}
          <input {...getInputProps()} />
          <Button
            {...getRootProps()}
            variant="outlined"
            startIcon={<FaFileUpload />}
            size="large"
          >
            {t("open_gallery", { defaultValue: "Open Gallery" })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaCamera />}
            onClick={openCamera}
            size="large"
          >
            {t("open_camera", { defaultValue: "Open Camera" })}
          </Button>
        </Stack>
      </Drawer>

      <Dialog open={isCameraOpen} onClose={closeCamera} fullScreen>
        <DialogContent sx={{ p: 0, m: 0, position: "relative" }}>
          <Webcam
            audio={false}
            ref={webcamRef}
            onUserMediaError={handleCameraError}
            screenshotFormat="image/jpeg"
            //  videoConstraints={{ facingMode: { exact: "environment" } }}
            videoConstraints={{facingMode: "user"}}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCamera}>
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button onClick={handleCapture} variant="contained">
            {t("capture", { defaultValue: "Capture" })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FileUploaderWithCamera;
